import logger from "@/core/logger"
import settings from "./settings"
import windows from "./windows"
import { toError } from "@/utils/toError"

const { performance } = windows.site

type ValidName = `nosto.${string}`

const noop = () => {}

function startMeasuringPerformance(name: ValidName) {
  performance.mark(`${name}.start`)

  return () => {
    performance.mark(`${name}.end`)
    performance.measure(`${name}`, `${name}.start`, `${name}.end`)
  }
}

function resolveFunction(): typeof startMeasuringPerformance {
  if (settings.measurePerformance) {
    if (
      typeof performance === "object" &&
      typeof performance.mark === "function" &&
      typeof performance.measure === "function"
    ) {
      return startMeasuringPerformance
    } else {
      return () => {
        logger.debug("Browser doesn't support user timing API.")
        return noop
      }
    }
  } else {
    return () => {
      logger.debug("Performance measuring not enabled.")
      return noop
    }
  }
}

export const startMeasuring = resolveFunction()

async function handlePromise<T>(promise: Promise<T>, end: () => void) {
  try {
    const result = await promise
    end()
    return result
  } catch (e) {
    end()
    throw toError(e)
  }
}

/**
 * Measure the performance of some code.
 * @param {String} name The name of the measure to be recorded.
 * @param {Function} callback the code to measure
 * @return {Object} The result returned by callback
 */
export default function measure<T>(name: ValidName, callback: () => T): T {
  if (!settings.measurePerformance) {
    return callback()
  }
  const end = startMeasuring(name)

  let result
  try {
    result = callback()
  } catch (e) {
    end()
    throw toError(e)
  }

  // detect and handle promise
  if (result instanceof Promise) {
    void handlePromise(result, end)
  } else {
    end()
  }

  return result
}
